import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/components/auth/AuthProvider';
import { useTrainingLoadAnalysis } from './useTrainingLoadAnalysis';
import { supabase } from '@/integrations/supabase/client';
import { getDateRangeFilter, getDataLimitForTimeRange, type TimeRange } from '@/lib/utils/dateRange';

export const useRunSuggestion = (timeRange: TimeRange) => {
  const { user } = useAuth();

  // Get comprehensive training load and heart rate analysis
  const { data: trainingLoadData, isLoading: trainingLoadLoading } = useTrainingLoadAnalysis(30);
  
  // Fetch recent running data and other activity data for AI analysis
  const { data: suggestionData, isLoading: suggestionLoading, error } = useQuery({
    queryKey: ['run-suggestion', user?.id, timeRange, trainingLoadData],
    queryFn: async () => {
      if (!user) return null;

      const dateFilter = getDateRangeFilter(timeRange);
      const dataLimit = getDataLimitForTimeRange(timeRange);

      // Fetch core datasets in parallel for latency reduction
      const [
        recentRuns,
        stravaRuns,
        generalFitness,
        fitnessGoals,
        userProfile,
      ] = await Promise.all([
        supabase
          .from('running_performance')
          .select('*')
          .gte('recorded_date', dateFilter.startDate)
          .lte('recorded_date', dateFilter.endDate)
          .order('recorded_date', { ascending: false })
          .limit(dataLimit)
          .then(({ data }) => data),
        supabase
          .from('running_performance')
          .select('*')
          .eq('data_source', 'strava')
          .gte('recorded_date', dateFilter.startDate)
          .lte('recorded_date', dateFilter.endDate)
          .order('recorded_date', { ascending: false })
          .limit(dataLimit)
          .then(({ data }) => data),
        supabase
          .from('general_fitness')
          .select('*')
          .gte('recorded_date', dateFilter.startDate)
          .lte('recorded_date', dateFilter.endDate)
          .order('recorded_date', { ascending: false })
          .limit(dataLimit)
          .then(({ data }) => data),
        supabase
          .from('fitness_goals')
          .select('*')
          .eq('status', 'active')
          .eq('type', 'running')
          .then(({ data }) => data),
        supabase
          .from('profiles')
          .select('fitness_level, experience_level, primary_goals')
          .eq('user_id', user.id)
          .maybeSingle()
          .then(({ data }) => data),
      ]);


      // Calculate last run date
      const lastRunDate = recentRuns && recentRuns.length > 0 
        ? recentRuns[0].recorded_date 
        : new Date().toISOString().split('T')[0];

      // Call the enhanced edge function with heart rate data
      const { data: aiSuggestion, error: aiError } = await supabase.functions.invoke('generate-run-suggestion', {
        body: {
          recentRuns: recentRuns || [],
          stravaRuns: stravaRuns || [],
          generalFitness: generalFitness || [],
          fitnessGoals: fitnessGoals || [],
          userProfile: userProfile || {},
          lastRunDate,
          timeRange,
          dateFilter,
          // Include new training load and heart rate data
          trainingLoadData: trainingLoadData || null,
          heartRateMetrics: trainingLoadData?.heartRateMetrics || null,
          currentStatus: trainingLoadData?.currentStatus || null
        }
      });

      if (aiError) {
        console.error('AI suggestion error:', aiError);
        throw aiError;
      }

      return aiSuggestion?.suggestion || null;
    },
    enabled: !!user && !trainingLoadLoading,
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false
  });

  return {
    suggestionData,
    isLoading: suggestionLoading || trainingLoadLoading,
    error,
    trainingLoadData
  };
};